import { useMemo } from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { DateStatusBox } from '@/components/DateStatusBox';
import { Loader } from '@/components/Loader';
import { OverflowText } from '@/components/OverflowText';
import { Tooltip } from '@/components/Tooltip';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import { SimpleTags } from '@/sections/AddTag';
import { SeedStatusComponent } from '@/sections/seeds/components/badges/SeedStatus';
import { Seed } from '@/types';
import { getDisplayOriginText } from '@/utils/originDisplay.util';
import { Regex } from '@/utils/regex.util';

/**
 * Props for the SeedDrawerHeader component
 */
interface SeedDrawerHeaderProps {
  /** The seed object to display in the header */
  seed: Seed;
  /** Handler for removing tags - receives the tag string being removed */
  onRemoveTag?: (tag: string) => void;
}

/**
 * SeedDrawerHeader Component
 *
 * Displays the header section of the seed drawer, including:
 * - Seed identifier with copy-to-clipboard functionality
 * - Source/origin information and seed type
 * - Seed status badge (Approved, Pending, Rejected, Frozen, Deleted)
 * - First seen and last seen timestamps
 *
 * Note: Action buttons are now provided by the SeedsActions toolbar component
 * below the header, following the unified pattern from Technology/Assets sections.
 *
 * @example
 * ```tsx
 * <SeedDrawerHeader seed={seed} />
 * ```
 */
export function SeedDrawerHeader({ seed, onRemoveTag }: SeedDrawerHeaderProps) {
  // Fetch filter options for Chariot rollup pattern
  const { data: filterOptions } = useGetVulnerabilityFilters();

  // Memoize sourceFilterOptions to prevent unnecessary re-renders
  const sourceFilterOptions = useMemo(
    () => filterOptions?.sourceFilters || [],
    [filterOptions?.sourceFilters]
  );

  // Determine seed type for display
  const isIP = useMemo(
    () =>
      seed?.identifier
        ? Boolean(
            Regex.IPV4.test(seed.identifier) || Regex.IPV6.test(seed.identifier)
          )
        : false,
    [seed?.identifier]
  );

  const isWebapp = seed?.class === 'webapplication';

  // Get the display type string
  const seedTypeLabel = isWebapp
    ? 'Web Application'
    : isIP
      ? 'IP Address'
      : 'Domain';

  // Apply Chariot rollup pattern to origin display
  const displayOrigin = useMemo(
    () => getDisplayOriginText(seed.origin, sourceFilterOptions),
    [seed.origin, sourceFilterOptions]
  );

  // Check if origin is an email to show tooltip
  const isEmail = seed.origin?.includes('@');

  return (
    <div
      data-testid="seed-details-header"
      className="flex items-start justify-between gap-6"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {/* Identifier and source - tightly coupled */}
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">
            <CopyToClipboard alwaysShowCopyIcon>
              <OverflowText text={seed.identifier} />
            </CopyToClipboard>
          </h1>
          <div className="text-chariot-text-secondary flex items-center gap-1 text-sm">
            {isEmail ? (
              <Tooltip title={seed.origin}>{displayOrigin}</Tooltip>
            ) : (
              displayOrigin || 'Unknown Source'
            )}{' '}
            • {seedTypeLabel}
          </div>
        </div>

        {/* Tags - logically grouped with identity */}
        {seed.tags && seed.tags.length > 0 && onRemoveTag && (
          <SimpleTags
            tags={seed.tags}
            onRemove={onRemoveTag}
            variant="compact"
          />
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-3">
        <div className="flex items-start gap-2">
          <div className="bg-chariot-background-secondary flex h-[88px] min-w-[153px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-4">
            <p className="text-chariot-text-primary text-xs font-semibold">
              Seed Status
            </p>
            <Loader
              isLoading={false}
              className="h-[26px] w-[70px] rounded-full"
            >
              {seed && seed.key && <SeedStatusComponent seed={seed} />}
            </Loader>
          </div>
          {seed.created && seed.visited && (
            <DateStatusBox firstSeen={seed.created} lastSeen={seed.visited} />
          )}
        </div>
      </div>
    </div>
  );
}
